import { reaction } from 'mobx';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';
import TextareaAutosize from 'react-autosize-textarea/lib';
import styled, { css } from 'styled-components';

import { useStores } from '../../stores';
import { detectBrowser } from '../../utilities';
import Scrollbar from '../Scrollbar';
import { inputStyles } from './styles';

const Input = styled.input`
  ${inputStyles};
`;

const Textarea = styled(TextareaAutosize)`
  ${inputStyles};
  resize: none;
  overflow: hidden;
`;

const InputBase = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedby,
      autoComplete,
      autoFocus,
      id,
      name,
      className,
      placeholder,
      inputProps,
      renderSuffix,
      onChange,
      onBlur,
      onClick,
      onKeyDown,
      onKeyUp,
      onFocus,
      readOnly,
      disabled,
      defaultValue,
      required,
      type = 'text',
      multiline,
      rows,
      maxRows,
      maxHeight = 160,
      value,
      ...props
    },
    ref
  ) => {
    const {
      config: { scrollBar, messageInput },
      ui,
      chatWindow
    } = useStores();
    const paddingSide = messageInput.setGeneralSideSpace ? '16px' : null;
    const { isFirefox } = detectBrowser;
    const [forceRerender, setForceRerender] = useState(true);
    let InputComponent = Input;

    /**
     * Override component.
     */
    if (multiline) {
      InputComponent = Textarea;
    }

    /**
     * If prop multiline = `true`, we use PubSub to track chat window resize.
     * This is necessary for the correct resizing of textarea
     * (by default, it only tracks resize of the browser window).
     */
    useEffect(() => {
      if (multiline) {
        return reaction(
          () => [chatWindow.dimensions.width, chatWindow.dimensions.height],
          () => setForceRerender(!forceRerender)
        );
      }

      return () => {};
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceRerender, multiline]);

    return (
      <Root className={className} {...props}>
        <Scrollbar autoHeight autoHeightMax={maxHeight} {...scrollBar}>
          <InputComponentWrap
            isMobile={ui.isMobile}
            isFirefox={isFirefox}
            specialPaddingLeft={paddingSide}
          >
            <InputComponent
              ref={ref}
              aria-describedby={ariaDescribedby}
              aria-label={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              id={id}
              name={name}
              disabled={disabled}
              defaultValue={defaultValue}
              onChange={onChange}
              onBlur={onBlur}
              onClick={onClick}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              onFocus={onFocus}
              placeholder={placeholder}
              readOnly={readOnly}
              required={required}
              type={type}
              value={value}
              rows={rows}
              maxRows={maxRows}
              async={multiline}
              {...inputProps}
            />
          </InputComponentWrap>
        </Scrollbar>
        {renderSuffix && (
          <SuffixWrap isMobile={ui.isMobile} specialPaddingRight={paddingSide}>
            {renderSuffix}
          </SuffixWrap>
        )}
      </Root>
    );
  }
);

InputBase.displayName = 'InputBase';

InputBase.propTypes = {
  /**
   * The id of the input element.
   */
  id: PropTypes.string,
  /**
   * Name attribute of the input element.
   */
  name: PropTypes.string,
  /**
   * @ignore
   */
  'aria-describedby': PropTypes.string,
  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill.
   * You can learn more about it following the specification.
   */
  autoComplete: PropTypes.string,
  /**
   * If true, the input element will be focused during the first mount.
   */
  autoFocus: PropTypes.bool,
  /**
   * Override or extend the styles applied to the component.
   */
  className: PropTypes.string,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If true, the input element will be readonly.
   * It prevents the user from changing the value of the field
   * (not from interacting with the field).
   */
  readOnly: PropTypes.bool,
  /**
   * If true, the input element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If true, the label is displayed as required and the input element` will be required.
   */
  required: PropTypes.bool,
  /**
   * Type of the input element. It should be a valid HTML5 input type.
   */
  type: PropTypes.string,
  /**
   * If true, a textarea element will be rendered instead of an input.
   */
  multiline: PropTypes.bool,
  /**
   * 	Number of rows to display when multiline option is set to true.
   */
  rows: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  /**
   * Maximum number of rows to display when multiline option is set to true.
   */
  maxRows: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  /**
   * Maximum height (if more than specified - scrollbar is used).
   */
  maxHeight: PropTypes.number,
  /**
   * Initialize autosize asynchronously.
   * Enable it if you are using StyledComponents.
   * This is forced to true when maxRows is set.
   * Async initialization will make your page "jump" when the component appears,
   * as it will first be rendered with the normal size, then resized to content.
   */
  async: PropTypes.bool,
  /**
   * The default value of the input element.
   */
  defaultValue: PropTypes.any,
  /**
   * Input value.
   */
  value: PropTypes.any,
  /**
   * Attributes applied to the input element.
   */
  inputProps: PropTypes.object,
  /**
   * @ignore
   */
  renderSuffix: PropTypes.node,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onFocus: PropTypes.func
};

const Root = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  line-height: 19px;
  font-size: 16px;
  cursor: auto;
`;

const InputComponentWrap = styled.label`
  display: block;
  padding: 10px 2px 10px 30px;
  margin: 0;
  font: inherit;
  transition: none;
  ${({ isMobile, specialPaddingLeft }) => {
    if (isMobile) {
      return css`
        padding-left: 20px;
      `;
    }

    if (specialPaddingLeft) {
      return css`
        padding-left: ${props => props.specialPaddingLeft};
      `;
    }

    return null;
  }}

  /* The many lines of the placeholder does not display correctly in Firefox */
  ${({ isFirefox }) => {
    if (isFirefox) {
      return css`
        textarea::placeholder {
          line-height: 1.5;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
      `;
    }

    return null;
  }}
`;

const SuffixWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 30px 10px 0;
  ${({ isMobile, specialPaddingRight }) => {
    if (isMobile) {
      return css`
        padding-right: 20px;
      `;
    }

    if (specialPaddingRight) {
      return css`
        padding-right: ${props => props.specialPaddingRight};
      `;
    }

    return null;
  }}
`;

export default InputBase;
